import React from "react";
import { FormInstance } from "antd";
import { MessageApi } from "antd/lib/message";
import { IDocumentsData, IProcessesData, IRegisterActiveItem, ISaleChannelData, TrailerType } from "store/lobby/lobby-model";
import { IUserData } from "store/user-session/user-session-model";
import { DrawerStatusEnum } from "typings/global.types";
import { mountArrayOffNotEmpty, mountArrayOfDocuments, mountArrayDrivers, searchProcessByIdSelected, searchSaleChannelByIdSelected } from "utils/functions/";
import useTranslation from "utils/hooks/useTranslation";
import { ConfirmCancelEdit } from "./components/confirm";
import * as S from "./styled/screen.styled";

export interface IVehicleAction {
  id: number;
  dock?: string;
}

type IOnConfirmDrawerInbound = {
  form: FormInstance<any>;
  userData: IUserData;
  eventTime: string;
  message: MessageApi;
  messageDescription: string;
  messageErrorDuplicateDocuments: string;
  documents: Array<IDocumentsData>;
  inboundProcessesList: Array<IProcessesData>;
  saleChannelActive: number;
  salesChannelList: Array<ISaleChannelData>;
  processActive: number;
  lobbySaveInboundAsync: (...args) => void;
};

type IOnConfirmDrawerOutbound = {
  form: FormInstance<any>;
  outboundProcessActive: number;
  outboundProcessesList: Array<IProcessesData>;
  eventTime: string;
  yardItemId?: number;
  lobbySaveOutboundAsync;
  message: MessageApi;
  messageDescription: string;
  messageErrorDuplicateDocuments: string;
  documents: Array<IDocumentsData>;
};

type IOnConfirmDrawerViewRegister = {
  visibleDrawerViewMode?: DrawerStatusEnum;
  form: FormInstance<any>;
  message: MessageApi;
  messageDescription: string;
  documents: Array<IDocumentsData>;
  inboundProcessesList: Array<IProcessesData>;
  messageErrorDuplicateDocuments: string;
  userData: IUserData;
  processActive: number;
  saleChannelActive: number;
  salesChannelList: Array<ISaleChannelData>;
  lobbyUpdateDrawerViewMode: (...args) => void;
  lobbyUpdateInboundAsync: (...args) => void;
  lobbyClearDocuments: (...args) => void;
};

type IOnConfirmDrawerInboundFromCheckin = {
  form: FormInstance<any>;
  userData: IUserData;
  eventTime: string;
  message: MessageApi;
  messageDescription: string;
  documents: Array<IDocumentsData>;
  checkinRegisterActive?: IRegisterActiveItem;
  inboundProcessesList: Array<IProcessesData>;
  messageErrorDuplicateDocuments: string;
  processActive: number;
  saleChannelActive: number;
  salesChannelList: Array<ISaleChannelData>;
  lobbySaveInboundAsync: (...args) => void;
};

type IOnConfirmFinishVehicleRegister = {
  registerActive: IRegisterActiveItem;
  message: MessageApi;
  messageDescription: string;
  lobbyFinishVehicleRegisterAsync: (...args) => void;
};

export const onConfirmDrawerInbound = async ({
  form,
  userData,
  eventTime,
  lobbySaveInboundAsync,
  message,
  messageDescription,
  messageErrorDuplicateDocuments,
  documents,
  inboundProcessesList,
  saleChannelActive,
  salesChannelList,
  processActive,
}: IOnConfirmDrawerInbound) => {
  try {
    const formaData = await form.validateFields();
    const { carrier, vehiclePlate, trailersPlates, observation, processDescription, cpf1, driver1, trailerType } = formaData;
    const process = !isNaN(processDescription) ? searchProcessByIdSelected(processDescription, inboundProcessesList) : processDescription;
    const saleChannelDescription = searchSaleChannelByIdSelected(saleChannelActive, salesChannelList);
    const documentFormated = mountArrayOfDocuments(documents, formaData);
    if (!validateDocumentsDuplicates(documentFormated)) {
      const bodyRequest = {
        carrier,
        vehiclePlate: vehiclePlate.toUpperCase(),
        trailersPlates: mountArrayOffNotEmpty(trailersPlates),
        observation,
        eventTime,
        user: null,
        locationId: +userData?.locationId,
        processId: processActive,
        processDescription: process,
        saleChannel: checkIfSaleChannelIsNull(saleChannelActive, saleChannelDescription),
        drivers: mountArrayDrivers(cpf1, driver1),
        documents: documentFormated,
        ...mountTrailerType(trailerType),
      };
      lobbySaveInboundAsync(bodyRequest);
    } else {
      message.error(messageErrorDuplicateDocuments);
    }
  } catch (e) {
    console.error(new Error("Inbound form validation error"));
    message.error(messageDescription);
  }
};

export const onConfirmDrawerOutbound = async ({
  form,
  outboundProcessActive,
  outboundProcessesList,
  eventTime,
  yardItemId,
  lobbySaveOutboundAsync,
  message,
  messageDescription,
  messageErrorDuplicateDocuments,
  documents,
}: IOnConfirmDrawerOutbound) => {
  try {
    const formaData = await form.validateFields();
    const { trailersPlates, observation, processDescription } = formaData;
    const process = !isNaN(processDescription) === true ? searchProcessByIdSelected(processDescription, outboundProcessesList) : processDescription;
    const documentFormated = mountArrayOfDocuments(documents, formaData);
    if (!validateDocumentsDuplicates(documentFormated)) {
      const bodyRequest = {
        trailersPlates: mountArrayOffNotEmpty(trailersPlates),
        observation,
        eventTime,
        user: null,
        outboundProcessId: outboundProcessActive,
        outboundProcessDescription: process,
        yardRegisterId: yardItemId,
        documents: documentFormated,
      };
      lobbySaveOutboundAsync(bodyRequest);
    } else {
      message.error(messageErrorDuplicateDocuments);
    }
  } catch (e) {
    console.error(new Error("Outbound form validation error"));
    message.error(messageDescription);
  }
};

export const onConfirmDrawerViewRegister = async ({
  visibleDrawerViewMode,
  lobbyUpdateDrawerViewMode,
  form,
  lobbyUpdateInboundAsync,
  message,
  messageDescription,
  documents,
  inboundProcessesList,
  messageErrorDuplicateDocuments,
  userData,
  processActive,
  saleChannelActive,
  salesChannelList,
}: IOnConfirmDrawerViewRegister) => {
  if (visibleDrawerViewMode === DrawerStatusEnum.View) {
    lobbyUpdateDrawerViewMode(DrawerStatusEnum.Edit);
    return;
  }
  try {
    const formaData = await form.validateFields();
    const { carrier, vehiclePlate, trailersPlates, observation, processDescription, cpf1, driver1, trailerType } = formaData;
    const process = !isNaN(processDescription) === true ? searchProcessByIdSelected(processDescription, inboundProcessesList) : processDescription;
    const documentFormated = mountArrayOfDocuments(documents, formaData);
    const saleChannelDescription = searchSaleChannelByIdSelected(saleChannelActive, salesChannelList);
    if (!validateDocumentsDuplicates(documentFormated)) {
      const bodyRequest = {
        carrier,
        vehiclePlate: vehiclePlate.toUpperCase(),
        trailersPlates: mountArrayOffNotEmpty(trailersPlates),
        processId: processActive,
        processDescription: process,
        user: null,
        observation,
        locationId: +userData?.locationId,
        saleChannel: checkIfSaleChannelIsNull(saleChannelActive, saleChannelDescription),
        drivers: mountArrayDrivers(cpf1, driver1),
        documents: documentFormated,
        ...mountTrailerType(trailerType),
      };
      lobbyUpdateInboundAsync(bodyRequest, form);
    } else {
      message.error(messageErrorDuplicateDocuments);
    }
  } catch (e) {
    console.error(new Error("Edit form validation error"));
    message.error(messageDescription);
  }
};

export const onConfirmDrawerInboundFromCheckin = async ({
  form,
  userData,
  eventTime,
  lobbySaveInboundAsync,
  message,
  messageDescription,
  documents,
  checkinRegisterActive,
  inboundProcessesList,
  messageErrorDuplicateDocuments,
  processActive,
  saleChannelActive,
  salesChannelList,
}: IOnConfirmDrawerInboundFromCheckin) => {
  try {
    const formaData = await form.validateFields();
    const { carrier, vehiclePlate, trailersPlates, observation, processDescription, cpf1, driver1, trailerType } = formaData;
    const process = !isNaN(processDescription) === true ? searchProcessByIdSelected(processDescription, inboundProcessesList) : processDescription;
    const saleChannelDescription = searchSaleChannelByIdSelected(saleChannelActive, salesChannelList);
    const documentFormated = mountArrayOfDocuments(documents, formaData);
    if (!validateDocumentsDuplicates(documentFormated)) {
      const bodyRequest = {
        user: null,
        carrier,
        vehiclePlate: vehiclePlate.toUpperCase(),
        trailersPlates: mountArrayOffNotEmpty(trailersPlates),
        eventTime,
        processId: processActive,
        processDescription: process,
        observation,
        locationId: +userData?.locationId,
        saleChannel: checkIfSaleChannelIsNull(saleChannelActive, saleChannelDescription),
        drivers: mountArrayDrivers(cpf1, driver1),
        documents: documentFormated,
        yardRegisterId: checkinRegisterActive?.yardRegisterId,
        ...mountTrailerType(trailerType),
      };
      lobbySaveInboundAsync(bodyRequest);
    } else {
      message.error(messageErrorDuplicateDocuments);
    }
  } catch (e) {
    console.error(new Error("Inbound form validation error"));
    message.error(messageDescription);
  }
};

export const onConfirmVehicleAction = async ({ form, id, message, messageDescription, vehicleActionSelected, lobbySaveVehicleActionAsync }) => {
  try {
    const formaData = await form.validateFields();
    const { dock } = formaData;
    const optionDockInboundSelected = 2;
    let bodyRequest: IVehicleAction = { id: id };
    if (vehicleActionSelected === optionDockInboundSelected) {
      bodyRequest = { ...bodyRequest, dock };
    }
    lobbySaveVehicleActionAsync(bodyRequest);
  } catch (e) {
    console.error(new Error("Vehicle action error"));
    message.error(messageDescription);
  }
};

export const onConfirmFinishVehicleAction = async ({ registerActive, message, messageDescription, lobbyFinishVehicleRegisterAsync }: IOnConfirmFinishVehicleRegister) => {
  try {
    const bodyRequest = {
      yardRegisterId: registerActive?.yardRegisterId,
    };
    lobbyFinishVehicleRegisterAsync(bodyRequest);
  } catch (e) {
    console.error(new Error("Finish vehicle error"));
    message.error(messageDescription);
  }
};

const isConfirmModalOpen = (visibleDrawerViewMode, formViewOrEditModified) => visibleDrawerViewMode === DrawerStatusEnum.Edit && formViewOrEditModified === true;

export const OnConfirmCancelEdit = ({ handleOnConfirm, okText, cancelText, visibleDrawerViewMode, formViewOrEditModified, lobbyHideDrawerViewRegister, disabled }) => {
  const CANCEL = useTranslation({ id: "cancel" });
  return isConfirmModalOpen(visibleDrawerViewMode, formViewOrEditModified) ? (
    ConfirmCancelEdit({
      handleOnConfirm: handleOnConfirm,
      okText: okText,
      cancelText: cancelText,
      disabled: disabled,
    })
  ) : (
    <S.CancelButton htmlType="submit" onClick={lobbyHideDrawerViewRegister} disabled={disabled}>
      {CANCEL}
    </S.CancelButton>
  );
};

export const registersIsNotEmpty = (registers) => registers?.length > 0;

export const onChangeSizePage = (size) => (size.width <= 1024 ? "center" : "start");

export const checkIfSaleChannelIsNull = (saleChannelActive, saleChannelDescription) =>
  saleChannelActive === null ? null : { id: saleChannelActive, description: saleChannelDescription };

export const mountTrailerType = (trailerType?: number) => {
  if (trailerType === undefined) return {};
  if (trailerType == null || !Object.values(TrailerType).includes(trailerType)) return { trailerType: null };
  return { trailerType };
};

export const validateDocumentsDuplicates = (documents) =>
  documents.some((doc1, index) => documents.slice(index + 1).some((doc2) => doc1.type === doc2.type && doc1.value === doc2.value));
